import { useTranslation } from 'react-i18next';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import { Icon } from '../Icon/Icon';
import { ResponsiveMenu } from '../ResponsiveMenu/ResponsiveMenu';
import MenuContainer from '../MenuContainer/MenuContainer';
import LogoutButton from '../LogOutButton/LogOutButton';
import CONFIG from '../../config/config';
import { isSupportedLanguage } from '../../shared/supported-languages';
import store from '../../assets/images/store.svg';
import catalogues from '../../assets/images/catalogue.svg';
import settings from '../../assets/images/settings.svg';
import orders from '../../assets/images/orders.svg';
import box from '../../assets/images/box.svg';
import chartBar from '../../assets/images/chart-bar.svg';
import { useSelector } from 'react-redux';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { useState } from 'react';
import { Modal } from '../Modal/Modal';
import check from '../../assets/images/check.svg';
import { BolderText } from '../BolderText/BolderText';
import LoginFlow from '../LoginFlow/LoginFlow';
import { ActivePricePlan } from '../ActivePricePlan/ActivePricePlan';

function MainMenu() {
  const { t, i18n } = useTranslation();
  const isLoggedIn = useSelector((state: any) => state.settings.value.isLoggedIn);
  const [displayLoginSignUpFlow, setDisplayLoginSignUpFlow] = useState(false);

  const languages = CONFIG.SUPPORTED_LANGUAGES.map(l => {
    return {
      code: l.code,
      name: t(`languages.${l.code}`),
      onClick: () => i18n.changeLanguage(l.code),
      icon: <Icon url={l.icon} />,
    }
  })

  const menuItems = [
    { text: t('menu.store')!, href: '/', isDisabled: false, flags: ['see-meetings', 'see-contracts'], 
    icon: <><Icon url={store} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('menu.calendar')!, href: '/calendar', flags: ['see-meetings'], isDisabled: false},
        { text: t('menu.calendar_app'), href: '/', flags: [], isDisabled: true},
        { text: t('menu.contracts')!, href: '/contracts', flags: ['see-contracts'], isDisabled: false},
        { text: t('menu.customers')!, href: '/customers', flags: ['see-customers'], isDisabled: false},
      ] 
    },
    { 
      text: t('menu.orders')!, href: '/orders', flags: ['see-orders', 'see-self-orders'],
      icon: <><Icon url={orders} />&nbsp;&nbsp;</>,  
    },
    { text: t('menu.marketing')!, href: '/', isDisabled: false, flags: ['see-marketing-collections', 'see-leads'], 
    icon: <><Icon url={catalogues} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('admin.menu.collections')!, href: '/catalogues', isDisabled: false, flags: ['see-marketing-collections'] },
        { text: t('menu.leads')!, href: '/leads', isDisabled: false, flags: ['see-leads'] },
        { text: t('menu.socials'), href: '/', isDisabled: true, flags: [] },
        { text: t('menu.tablet')!, href: '/tablet', isDisabled: true, flags: ['see-marketing-collections'] },
      ] 
    },
    { text: t('menu.production')!, href: '/production', flags: ['see-providers'], 
    icon: <><Icon url={box} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('production.menu.providers')!, href: '/providers', isDisabled: false, flags: ['see-providers']},
        { text: t('production.menu.stock')!, href: '/stock', isDisabled: false, flags: ['see-stock']},
        { text: t('production.menu.dresses')!, href: '/inventory', isDisabled: false, flags: ['see-dresses']}
      ] 
    },
    { text: t('menu.stats')!, href: '/', isDisabled: false, flags: ['see-employees'], 
      icon: <><Icon url={chartBar} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('Orders')!, href: '/insights/orders', isDisabled: false, flags: ['see-employees'] },
        { text: t('PnL')!, href: '/insights/pnl', isDisabled: true, flags: ['see-employees'] },
        { text: t('Tablet')!, href: '/insights/tablet', isDisabled: true, flags: ['see-employees'] },
      ] 
    },
    { text: t('menu.admin')!, href: '/admin', flags: ['see-partners'], 
    icon: <><Icon url={settings} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('admin.menu.partners')!, href: '/partners', flags: ['see-partners'], isDisabled: false},
        { text: t('admin.menu.employees')!, href: '/employees', flags: ['see-employees'] },
        { text: t('menu.settings')!, href: '/settings', flags: ['see-employees'], isDisabled: false}
      ] 
    },
    {
      text: '',
      isDisabled: false, 
      href: '#',
      icon: isSupportedLanguage(i18n.language) ? 
        languages.filter(l => l.code === i18n.language)[0]?.icon
        : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon,
      submenuItems: languages.map((lang) => ({
        text: lang.name,
        onClick: () => i18n.changeLanguage(lang.code),
        isDisabled: false,
        icon: lang.icon
      })),
      flags: []
    },
  ];

  const notLoggedInMenuItems = [
    { text: t('menu.store')!, href: '', isDisabled: false, flags: [], 
    icon: <><Icon url={store} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('menu.calendar')!, href: '', flags: [], isDisabled: true},
        { text: t('menu.contracts')!, href: '', flags: [], isDisabled: true},
        { text: t('menu.customers')!, href: '', flags: [], isDisabled: true},
      ] 
    },
    { 
      text: t('menu.orders')!, href: '', flags: [], isDisabled: true,
      icon: <><Icon url={orders} />&nbsp;&nbsp;</>,  
    },
    { text: t('menu.marketing')!, href: '', isDisabled: false, flags: [], 
    icon: <><Icon url={catalogues} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('admin.menu.collections')!, href: '', isDisabled: true, flags: [] },
        { text: t('menu.leads')!, href: '', isDisabled: true, flags: [] },
        { text: t('menu.tablet')!, href: '', isDisabled: true, flags: [] },
      ] 
    },
    { text: t('menu.production')!, href: '', flags: [], 
    icon: <><Icon url={box} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('production.menu.providers')!, href: '', isDisabled: true, flags: []},
        { text: t('production.menu.stock')!, href: '', isDisabled: true, flags: []},
        { text: t('production.menu.dresses')!, href: '', isDisabled: true, flags: []}
      ] 
    },
    { text: t('menu.stats')!, href: '', isDisabled: false, flags: [], 
      icon: <><Icon url={chartBar} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('Tablet')!, href: '', isDisabled: true, flags: [] },
        { text: t('Orders')!, href: '', isDisabled: true, flags: [] },
        { text: t('PnL')!, href: '', isDisabled: true, flags: [] },
      ] 
    },
    { text: t('menu.admin')!, href: '', flags: [], 
    icon: <><Icon url={settings} />&nbsp;&nbsp;</>, 
      submenuItems: [
        { text: t('admin.menu.partners')!, href: '', flags: [], isDisabled: true},
        { text: t('admin.menu.employees')!, href: '', flags: [], isDisabled: true },
        { text: t('menu.settings')!, href: '', flags: [], isDisabled: true}
      ] 
    },
    {
      text: '',
      isDisabled: false, 
      href: '#',
      icon: isSupportedLanguage(i18n.language) ? 
        languages.filter(l => l.code === i18n.language)[0]?.icon
        : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon,
      submenuItems: languages.map((lang) => ({
        text: lang.name,
        onClick: () => i18n.changeLanguage(lang.code),
        isDisabled: false,
        icon: lang.icon
      })),
      flags: []
    },
  ];

  return (
    <MenuContainer>
      { isLoggedIn ? <>
        <div className="flex items-center flex-wrap">
          <ResponsiveMenu
            menuItems={menuItems.map((item) => ({
              ...item,
              element: (
                <SecondaryButton
                  key={item.text}
                  text={item.text}
                  isDisabled={item.isDisabled || false}
                  eventName={`Menu - Clicked ${item.text}`}
                />
              ),
            }))}
          />
          <ActivePricePlan />
          <LogoutButton />
        </div>
      </> : 
      <>
        <div className="flex items-center flex-wrap space-x-2">
          <ResponsiveMenu
            menuItems={notLoggedInMenuItems.map((item) => ({
              ...item,
              element: (
                <SecondaryButton
                  key={item.text}
                  text={item.text}
                  isDisabled={item.isDisabled || false}
                  eventName={`Guest Menu - Clicked ${item.text}`}
                />
              ),
            }))}
          />
          <PrimaryButton text={t("guest.login.cta")!} fn={() => setDisplayLoginSignUpFlow(prev => !prev)} />
        </div>
        { displayLoginSignUpFlow && 
          <Modal canClose modalTitle={t("guest.login.form.title")} onClose={() => setDisplayLoginSignUpFlow(false)}>
            <>
              <p>
                { t("guest.login.form.description") }.
              </p>
              <p><Icon url={check}></Icon>&nbsp; <BolderText text={t("guest.login.form.reason_1")!}/></p>
              <p><Icon url={check}></Icon>&nbsp; <BolderText text={t("guest.login.form.reason_2")!}/></p>
              <p><Icon url={check}></Icon>&nbsp; <BolderText text={t("guest.login.form.reason_3")!}/></p>
              <p><Icon url={check}></Icon>&nbsp; ... { t("guest.login.form.reason_others") } </p>
              <br/>
              <LoginFlow />
            </>
          </Modal> 
        }
      </> }
    </MenuContainer>
  );
}

export default MainMenu;
